<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    required: true,
  },
  // Cor do canal: pinta o disco do icone em repouso e toma o card no hover.
  tone: {
    type: String,
    default: 'slate',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isComingSoon: {
    type: Boolean,
    default: false,
  },
});

// Classes inteiras (nao montadas por string) pra o Tailwind enxergar cada uma.
const TONES = {
  slate: {
    disc: 'bg-n-slate-3 text-n-slate-11',
    discHover: 'group-hover:bg-n-slate-12 group-hover:text-n-solid-1',
    border: 'hover:border-n-slate-8',
    ring: 'group-hover:ring-n-slate-6',
  },
  brand: {
    disc: 'bg-n-brand/10 text-n-brand',
    discHover: 'group-hover:bg-n-brand group-hover:text-white',
    border: 'hover:border-n-brand',
    ring: 'group-hover:ring-n-brand/20',
  },
  iris: {
    disc: 'bg-n-iris-3 text-n-iris-11',
    discHover: 'group-hover:bg-n-iris-9 group-hover:text-white',
    border: 'hover:border-n-iris-8',
    ring: 'group-hover:ring-n-iris-5',
  },
  teal: {
    disc: 'bg-n-teal-3 text-n-teal-11',
    discHover: 'group-hover:bg-n-teal-9 group-hover:text-white',
    border: 'hover:border-n-teal-8',
    ring: 'group-hover:ring-n-teal-5',
  },
  blue: {
    disc: 'bg-n-blue-3 text-n-blue-11',
    discHover: 'group-hover:bg-n-blue-9 group-hover:text-white',
    border: 'hover:border-n-blue-8',
    ring: 'group-hover:ring-n-blue-5',
  },
  amber: {
    disc: 'bg-n-amber-3 text-n-amber-11',
    discHover: 'group-hover:bg-n-amber-9 group-hover:text-n-amber-12',
    border: 'hover:border-n-amber-8',
    ring: 'group-hover:ring-n-amber-5',
  },
  violet: {
    disc: 'bg-n-violet-3 text-n-violet-11',
    discHover: 'group-hover:bg-n-violet-9 group-hover:text-white',
    border: 'hover:border-n-violet-8',
    ring: 'group-hover:ring-n-violet-5',
  },
};

const tone = computed(() => TONES[props.tone] || TONES.slate);
const interactive = computed(() => !props.disabled && !props.isComingSoon);
</script>

<template>
  <button
    class="group relative flex flex-col items-start justify-start gap-5 rounded-2xl border border-solid border-n-weak bg-n-solid-1 px-5 py-6 text-start transition-all duration-200 ease-out"
    :class="[
      interactive
        ? [
            'cursor-pointer hover:-translate-y-0.5 hover:shadow-lg hover:shadow-n-slate-12/5',
            tone.border,
          ]
        : 'cursor-not-allowed',
      disabled && !isComingSoon ? 'opacity-60' : '',
    ]"
    :disabled="disabled"
  >
    <div
      class="flex size-11 items-center justify-center rounded-xl ring-4 ring-transparent transition-all duration-200 ease-out"
      :class="[tone.disc, interactive ? [tone.discHover, tone.ring] : '']"
    >
      <Icon :icon="icon" class="size-6" />
    </div>

    <div class="flex flex-col items-start gap-1.5">
      <h3
        class="flex items-center gap-1.5 text-sm font-medium capitalize text-n-slate-12"
      >
        {{ title }}
        <span
          v-if="interactive"
          class="i-lucide-arrow-right size-3.5 -translate-x-1 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100"
        />
      </h3>
      <p class="text-sm leading-relaxed text-n-slate-11">
        {{ description }}
      </p>
    </div>

    <div
      v-if="isComingSoon"
      class="absolute inset-0 flex cursor-not-allowed items-center justify-center rounded-2xl bg-gradient-to-br from-n-background/90 via-n-background/70 to-n-background/95 backdrop-blur-[2px]"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('CHANNEL_SELECTOR.COMING_SOON') }} 🚀
      </span>
    </div>
  </button>
</template>
